import { calcLineItemTotals } from "../proposalUtils";
import { GridCell, Calc, GmCalc, Tag, blankZero, gridKeyNav } from "./GridCell";
import { LibraryReviewTag } from "./LibraryReviewTag";
import { lineCost, gmPct, lineBadges } from "./gridMath";

const BADGE_TESTID = { SUB: "sub-badge", FLAT: "flat-rate-badge", Quote: "quote-badge", Incomplete: "incomplete-badge" };

export function GridLineRow({ item, scopeIdx, itemIdx, update, selected, onSelect, stock, changes, reviewable, cols, onApplyLib, onDismissLib }) {
  const t = calcLineItemTotals(item);
  const mode = item.pricing_mode || "standard";
  const isSub = mode === "subcontract", isFlat = mode === "flat_rate", std = !isSub && !isFlat;
  const auto = item.wc_key === "adder" || item.wc_key === "minimum";
  const qty = t.qty || 1;
  const id = `${scopeIdx}-${itemIdx}`;
  const cell = (c) => `grid-cell-${c}-${id}`;
  const flatPrice = parseFloat(item.flat_rate) || 0;
  const subCost = parseFloat(item.subcontract_cost) || 0;
  // Stock only matters for priced material; no cost → no stock concern. Zero on-hand is a heads-up, not an error.
  const hasMaterial = std && parseFloat(item.material_cost) > 0;
  const showStock = hasMaterial && stock != null;
  const stockTone = stock <= 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";

  return (
    <tr className={`cursor-pointer ${selected ? "bg-brand/10" : "hover:bg-slate-50"}`} data-testid={`line-item-${id}`} onClick={() => onSelect(item.id)} title="Click the row for description, location, freight and library details">
      <td className="w-6 border-b border-slate-200"></td>
      <td className="min-w-[170px] border-b border-slate-200 px-2 py-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {item.part_number && <span className="shrink-0 rounded bg-slate-100 px-1.5 py-px font-mono text-[10.5px] text-slate-500">{item.part_number}</span>}
          <input value={item.name || ""} onChange={(e) => update("name", e.target.value)} onClick={(e) => e.stopPropagation()} placeholder="Item name" disabled={auto} data-col="name"
            onKeyDown={(e) => {
              const atEnd = e.target.selectionEnd === e.target.value.length, atStart = e.target.selectionStart === 0;
              if (e.key === "ArrowLeft" && !atStart) return;
              if (e.key === "ArrowRight" && !atEnd) return;
              gridKeyNav(e, "name");
            }}
            className="h-7 min-w-0 flex-1 border-b border-transparent bg-transparent text-xs font-medium text-slate-800 outline-none placeholder:text-slate-400 focus:border-brand disabled:opacity-70"
            data-testid={`input-item-name-${id}`} />
          {lineBadges(item, t).map((b) => <Tag key={b} kind={b} testId={`${BADGE_TESTID[b]}-${id}`} />)}
          {reviewable && <LibraryReviewTag changes={changes} onApply={onApplyLib} onDismiss={onDismissLib} testId={`review-tag-${id}`} />}
        </div>
      </td>
      <td className="w-12 border-b border-slate-200 px-1">
        <GridCell col="qty" step="1" value={item.qty === 0 ? "" : item.qty} disabled={auto} testId={cell("qty")} title="Quantity — negative deducts (credit)"
          onChange={(v) => update("qty", v === "" || v === "-" ? v : (Number.isNaN(parseInt(v)) ? 1 : parseInt(v)))} />
      </td>
      {/* Material */}
      <td className="w-20 border-b border-l border-slate-200 px-1">
        {std && <GridCell col="cost" value={blankZero(item.material_cost)} onChange={(v) => update("material_cost", v)} testId={cell("cost")} title="Material cost per unit" />}
        {isSub && <GridCell col="cost" accent="sky" value={item.subcontract_cost ?? ""} disabled={auto} onChange={(v) => update("subcontract_cost", v)} testId={`input-sub-cost-${id}`} title="Sub cost per unit" />}
        {isFlat && <Calc value={null} />}
      </td>
      <td className="w-12 border-b border-slate-200 px-1">
        {std && <GridCell col="matgm" step="1" max="99" value={Math.round(parseFloat(item.material_margin) || 0)} onChange={(v) => update("material_margin", v)} testId={cell("matgm")} title="Material GM%" />}
        {isSub && <GmCalc value={gmPct(flatPrice, subCost)} testId={`sub-gm-${id}`} />}
        {isFlat && <Calc value={null} />}
      </td>
      <td className="w-20 border-b border-slate-200 px-1"><Calc value={std ? t.totalMat * qty : isSub ? subCost * qty : null} testId={`mat-total-${id}`} /></td>
      {/* Labor */}
      <td className="w-16 border-b border-l border-slate-200 px-1">
        {isSub ? <Calc value={null} /> : <GridCell col="hrs" step="0.25" value={blankZero(item.labor_hours)} onChange={(v) => update("labor_hours", v)} testId={cell("hrs")} title="Labor hours per unit" />}
      </td>
      {cols.hrsTotal && <td className="w-16 border-b border-slate-200 px-1"><Calc kind="hours" value={isSub ? null : t.hours * qty} /></td>}
      <td className="w-12 border-b border-slate-200 px-1">
        {std && <GridCell col="labgm" step="1" max="99" value={Math.round(parseFloat(item.labor_margin) || 0)} onChange={(v) => update("labor_margin", v)} testId={cell("labgm")} title="Labor GM%" />}
        {isFlat && <GmCalc value={gmPct(flatPrice, t.hours * t.labC)} testId={`flat-rate-gm-${id}`} />}
        {isSub && <Calc value={null} />}
      </td>
      {cols.rate && <td className="w-16 border-b border-slate-200 px-1"><Calc value={std ? t.rate : null} /></td>}
      <td className="w-20 border-b border-slate-200 px-1"><Calc value={std ? t.totalLab * qty : isFlat ? flatPrice * qty : null} testId={`lab-total-${id}`} /></td>
      {/* Price */}
      {cols.freight && <td className="w-16 border-b border-l border-slate-200 px-1"><Calc value={t.totalFreightForItem} /></td>}
      <td className={`w-24 border-b border-slate-200 px-1 ${cols.freight ? "" : "border-l"}`}>
        {std ? <Calc value={t.unitTotal} /> : auto ? <Calc value={flatPrice} /> : (
          <GridCell col="unit" accent={isFlat ? "amber" : "sky"} value={item.flat_rate ?? ""} onChange={(v) => update("flat_rate", v)}
            testId={isFlat ? `input-flat-rate-${id}` : `input-sub-price-${id}`} title={isFlat ? "Flat rate per unit" : "Price per unit"} />
        )}
      </td>
      <td className="w-20 border-b border-slate-200 px-1"><Calc strong value={t.grand} testId={isSub ? `sub-line-total-${id}` : `line-total-${id}`} /></td>
      <td className="w-12 border-b border-slate-200 px-1"><GmCalc value={gmPct(t.grand, lineCost(item, t))} testId={`line-gm-${id}`} /></td>
      {cols.stock && (
        <td className="w-12 border-b border-l border-slate-200 px-1 text-right" title="On-hand stock from Equipment Library">
          {showStock ? <span className={`inline-block min-w-[2rem] rounded px-1.5 py-px text-center text-[10px] font-semibold tabular-nums ${stockTone}`}>{stock}</span> : <span className="text-[10px] text-slate-300">—</span>}
        </td>
      )}
    </tr>
  );
}
